interface SearchEngineConfig {
  google: {
    apiKey: string;
    searchConsoleUrl: string;
  };
  bing: {
    apiKey: string;
    webmasterUrl: string;
  };
  indexNow: {
    key: string;
    keyLocation: string;
  };
}

class SearchEngineService {
  private config: SearchEngineConfig;

  constructor() {
    this.config = {
      google: {
        apiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
        searchConsoleUrl: 'https://searchconsole.googleapis.com/v1'
      },
      bing: {
        apiKey: process.env.REACT_APP_BING_API_KEY || '',
        webmasterUrl: 'https://www.bing.com/webmasters/api'
      },
      indexNow: {
        key: process.env.REACT_APP_INDEXNOW_KEY || 'moizwp-indexnow-key-2024',
        keyLocation: 'https://moizwp.com/moizwp-indexnow-key-2024.txt'
      }
    };
  }

  // Submit URLs to multiple search engines
  async submitToSearchEngines(urls: string[]): Promise<void> {
    try {
      console.log(`Submitting ${urls.length} URLs to search engines...`);

      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.log('Skipping search engine submission on localhost');
        return;
      }

      await Promise.allSettled([
        this.submitToGoogle(urls),
        this.submitToBing(urls),
        this.submitToIndexNow(urls)
      ]);

      console.log(`Search engine submission completed for ${urls.length} URLs`);
    } catch (error) {
      console.error('Error submitting to search engines:', error);
    }
  }

  // Submit to Google Search Console
  private async submitToGoogle(urls: string[]): Promise<void> {
    if (!this.config.google.apiKey) {
      console.warn('Google API key not configured - skipping Google submission');
      return;
    }

    try {
      // Note: Google Search Console API requires OAuth2 authentication
      // This is a simplified example - you'll need to implement OAuth2 flow
      console.log('Google Search Console submission requires OAuth2 setup');

      // Alternative: Use Google's Indexing API for news/content sites
      if (urls.length > 0) {
        await this.submitToGoogleIndexingAPI(urls[0]); // Submit first URL
      }
    } catch (error) {
      console.error('Error submitting to Google:', error);
    }
  }

  // Submit to Google Indexing API (for news/content sites)
  private async submitToGoogleIndexingAPI(url: string): Promise<void> {
    try {
      const response = await fetch(`https://indexing.googleapis.com/v3/urlNotifications:publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.google.apiKey}`
        },
        body: JSON.stringify({
          url: url,
          type: 'URL_UPDATED'
        })
      });

      if (response.ok) {
        console.log(`Successfully submitted to Google Indexing API: ${url}`);
      } else {
        console.warn(`Google Indexing API submission failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error submitting to Google Indexing API:', error);
    }
  }

  // Submit to Bing Webmaster Tools
  private async submitToBing(urls: string[]): Promise<void> {
    if (!this.config.bing.apiKey) {
      console.warn('Bing API key not configured - skipping Bing submission');
      return;
    }

    try {
      // Bing Webmaster Tools API submission
      // Note: This is a simplified example - actual implementation may vary
      console.log('Submitting to Bing Webmaster Tools...');

      // For now, we'll simulate the submission
      // In production, you'll need to implement the actual Bing API call
      console.log(`Bing submission simulated for: ${urls[0]}`);

      // Actual implementation would look like:
      /*
      const response = await fetch(`${this.config.bing.webmasterUrl}/SubmitUrl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': this.config.bing.apiKey
        },
        body: JSON.stringify({
          siteUrl: 'https://moizwp.com',
          url: urls[0] // Bing typically handles one URL at a time
        })
      });

      if (response.ok) {
        console.log(`Successfully submitted to Bing: ${urls[0]}`);
      }
      */

    } catch (error) {
      console.error('Error submitting to Bing:', error);
    }
  }

  // Submit to IndexNow (instant indexing for multiple search engines)
  private async submitToIndexNow(urls: string[]): Promise<void> {
    try {
      console.log('Submitting to IndexNow...');

      const response = await fetch('https://api.indexnow.org/indexnow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          host: 'moizwp.com',
          key: this.config.indexNow.key,
          keyLocation: this.config.indexNow.keyLocation,
          urlList: urls
        })
      });

      if (response.ok) {
        console.log(`Successfully submitted to IndexNow: ${urls.length} URLs`);

        // Log the response for debugging
        const responseText = await response.text();
        console.log('IndexNow response:', responseText);
      } else {
        console.warn(`IndexNow submission failed: ${response.status} ${response.statusText}`);

        // Log the error response for debugging
        const errorText = await response.text();
        console.error('IndexNow error response:', errorText);
      }
    } catch (error) {
      console.error('Error submitting to IndexNow:', error);
    }
  }

  // Get service status
  getStatus(): { google: boolean; bing: boolean; indexNow: boolean } {
    return {
      google: !!this.config.google.apiKey,
      bing: !!this.config.bing.apiKey,
      indexNow: !!this.config.indexNow.key
    };
  }

  // Get configuration details (for debugging)
  getConfig(): SearchEngineConfig {
    return {
      ...this.config,
      google: {
        ...this.config.google,
        apiKey: this.config.google.apiKey ? '***configured***' : 'not configured'
      },
      bing: {
        ...this.config.bing,
        apiKey: this.config.bing.apiKey ? '***configured***' : 'not configured'
      }
    };
  }
}

export const searchEngineService = new SearchEngineService();
export const submitToSearchEngines = (urls: string[]) => searchEngineService.submitToSearchEngines(urls);
